/**
 * Stage B/F — the canonical client doors to location truth. Every surface
 * (desktop, tablet, phone) reads and moves through these; none reinterprets
 * three nullable ticket columns.
 */
import type { GuidedStartBay, GuidedStartContext } from '@/lib/timer/interaction/guidedStart';
import { supabase } from '@/integrations/supabase/client';
import { fetchPersonDisplayNames } from '@/lib/people/personDisplayName';
import { normalizeSpotRows, rectOf, type FacilityLayout, type LayoutRow } from './facilityLayout';
import {
  currentLocationFromRow,
  indexCurrentLocations,
  locationPlanFromRow,
  moveRequestArgs,
  type CurrentLocationRow,
  type CurrentVehicleLocation,
  type LocationPlanRow,
  type LocationPlanSource,
  type MoverAttestation,
  type VehicleLocationPlan,
  type VehicleLocationTarget,
} from './model';

export const CURRENT_LOCATION_COLUMNS =
  'ticket_id, location_kind, bay_id, parking_spot_id, off_site_reason, source, confidence_class, confidence, observed_at, event_id';
export const LOCATION_PLAN_COLUMNS =
  'ticket_id, location_kind, bay_id, parking_spot_id, planned_for, source, reason';

/** Current physical truth for every vehicle in a shop — one narrow read. */
export async function readShopCurrentLocations(shopId: string): Promise<Map<string, CurrentVehicleLocation>> {
  const { data, error } = await supabase
    .from('vehicle_location_current')
    .select(CURRENT_LOCATION_COLUMNS)
    .eq('shop_id', shopId);
  if (error) throw error;
  return indexCurrentLocations((data ?? []) as CurrentLocationRow[]);
}

/**
 * Bays PHYSICALLY occupied right now (Part 9): a current BAY location. The
 * database keeps only live vehicles in a bay (vlc_bay_exclusive; a terminal
 * ticket's bay row is released), so no plan, rollover / specialty `bay_id`
 * encoding or technician default bay can appear here. Advisory for pickers —
 * the move authority re-decides at commit.
 */
export async function readShopOccupiedBayIds(shopId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('vehicle_location_current')
    .select('bay_id')
    .eq('shop_id', shopId)
    .eq('location_kind', 'BAY');
  if (error) throw error;
  return new Set(((data ?? []) as Array<{ bay_id: string | null }>).flatMap((r) => (r.bay_id ? [r.bay_id] : [])));
}

/**
 * Parking spots PHYSICALLY taken right now (#15): a current PARKING_SPOT
 * location. A terminal or picked-up ticket's row is released by the database
 * (tg_release_location_on_ticket_end / pickup = NOT_PRESENT), so this is the
 * same occupancy the move authority checks under its lock. Advisory for
 * pickers — the move authority re-decides at commit.
 */
export async function readShopOccupiedSpotIds(shopId: string): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from('vehicle_location_current')
    .select('ticket_id, parking_spot_id')
    .eq('shop_id', shopId)
    .eq('location_kind', 'PARKING_SPOT');
  if (error) throw error;
  const out = new Map<string, string>();
  for (const r of (data ?? []) as Array<{ ticket_id: string; parking_spot_id: string | null }>) {
    if (r.parking_spot_id) out.set(r.parking_spot_id, r.ticket_id);
  }
  return out;
}

export async function readShopLocationPlans(shopId: string): Promise<Map<string, VehicleLocationPlan>> {
  const { data, error } = await supabase
    .from('ticket_location_plan')
    .select(LOCATION_PLAN_COLUMNS)
    .eq('shop_id', shopId);
  if (error) throw error;
  const out = new Map<string, VehicleLocationPlan>();
  for (const r of (data ?? []) as LocationPlanRow[]) out.set(r.ticket_id, locationPlanFromRow(r));
  return out;
}

export async function readCurrentLocation(ticketId: string): Promise<CurrentVehicleLocation | null> {
  const { data, error } = await supabase
    .from('vehicle_location_current')
    .select(CURRENT_LOCATION_COLUMNS)
    .eq('ticket_id', ticketId)
    .maybeSingle();
  if (error) throw error;
  return data ? currentLocationFromRow(data as CurrentLocationRow) : null;
}

export async function readLocationPlan(ticketId: string): Promise<VehicleLocationPlan | null> {
  const { data, error } = await supabase
    .from('ticket_location_plan')
    .select(LOCATION_PLAN_COLUMNS)
    .eq('ticket_id', ticketId)
    .maybeSingle();
  if (error) throw error;
  return data ? locationPlanFromRow(data as LocationPlanRow) : null;
}

/** "Why does OptiBay think the vehicle is in Bay 4?" — newest first. */
export async function readLocationHistory(ticketId: string, limit = 100) {
  const { data, error } = await supabase.rpc('get_vehicle_location_history', {
    p_ticket_id: ticketId,
    p_limit: limit,
  });
  if (error) throw error;
  return data ?? [];
}

export type LocationActResult =
  /** capacityNotVerified (OD-33): placed on a lift with no rated capacity on record — allowed, not verified safe. */
  | { ok: true; replayed: boolean; capacityNotVerified?: boolean }
  | { ok: false; reason: string; liftSafetyBlock?: boolean };

function actResult(data: unknown, error: unknown): LocationActResult {
  if (error) return { ok: false, reason: (error as { message?: string }).message ?? 'Location change failed' };
  const r = (data ?? {}) as { success?: boolean; reason?: string; replayed?: boolean; liftSafetyBlock?: boolean; capacityNotVerified?: boolean };
  // Unknown = failure: only an explicit success moved anything.
  if (r.success !== true) {
    return { ok: false, reason: r.reason ?? 'Location change failed', liftSafetyBlock: r.liftSafetyBlock };
  }
  return { ok: true, replayed: r.replayed === true, ...(r.capacityNotVerified === true ? { capacityNotVerified: true } : {}) };
}

/** A physical move (any role allowed to relocate). `mover` = who physically moved it, as attested (L5). */
export async function moveVehicle(ticketId: string, target: VehicleLocationTarget, opts: {
  reason?: string; requestId?: string; mover?: MoverAttestation | null;
} = {}): Promise<LocationActResult> {
  const { data, error } = await supabase.rpc('move_vehicle_location', moveRequestArgs(ticketId, target, opts));
  return actResult(data, error);
}

/** An employee who may be named as the physical mover ("recorded on behalf of"), with their applicable role. */
export interface VehicleMoverCandidate { userId: string; moverRole: string; roles: string[]; displayName: string | null }

/**
 * The shop's OTHER employees a recorder may name as the mover (L5), with the canonical person name (roster →
 * professional identity → account; never an email). Empty when the caller may not relocate here.
 */
export async function readVehicleMoverCandidates(shopId: string): Promise<VehicleMoverCandidate[]> {
  const { data, error } = await supabase.rpc('list_vehicle_mover_candidates', { p_shop_id: shopId });
  if (error) throw error;
  const { data: session } = await supabase.auth.getSession();
  const me = session?.session?.user?.id ?? null;
  const rows = ((data ?? []) as Array<{ user_id: string; mover_role: string; roles: string[] | null }>)
    .filter((r) => r.user_id !== me);
  const names = await fetchPersonDisplayNames(supabase, rows.map((r) => r.user_id), shopId).catch(() => ({} as Record<string, string>));
  return rows.map((r) => ({ userId: r.user_id, moverRole: r.mover_role, roles: r.roles ?? [], displayName: names[r.user_id] ?? null }));
}

/** "OptiBay says Spot 12; the car is in Spot 8." Recorded as HUMAN_CORRECTION. */
export async function correctVehicleLocation(ticketId: string, target: VehicleLocationTarget, opts: {
  reason?: string; requestId?: string;
} = {}): Promise<LocationActResult> {
  const { data, error } = await supabase.rpc('correct_vehicle_location', {
    p_ticket_id: ticketId,
    p_location_kind: target.kind,
    p_bay_id: target.kind === 'BAY' ? target.bayId : undefined,
    p_parking_spot_id: target.kind === 'PARKING_SPOT' ? target.parkingSpotId : undefined,
    p_off_site_reason: target.kind === 'OFF_SITE' ? target.reason : undefined,
    p_reason: opts.reason,
    p_request_id: opts.requestId,
  });
  return actResult(data, error);
}

/** Plan where a vehicle should go. Cannot move it. */
export async function setLocationPlan(ticketId: string, plan: { bayId: string } | { parkingSpotId: string } | null, opts: {
  source?: LocationPlanSource; reason?: string; plannedFor?: string;
} = {}): Promise<LocationActResult> {
  const { data, error } = await supabase.rpc('set_ticket_location_plan', {
    p_ticket_id: ticketId,
    p_bay_id: plan && 'bayId' in plan ? plan.bayId : undefined,
    p_parking_spot_id: plan && 'parkingSpotId' in plan ? plan.parkingSpotId : undefined,
    p_source: opts.source ?? 'SCHEDULING',
    p_reason: opts.reason,
    p_planned_for: opts.plannedFor,
    p_clear: plan === null,
  });
  return actResult(data, error);
}

// ── Stage C — the facts Guided Start needs, read once per Start ──────────────

/**
 * Everything Guided Start decides from — current physical truth, the plan,
 * the shop's bays (with their technician), physical occupancy, and the vehicle
 * in words. Read fresh for every Start (and again after a refused bay).
 */
export async function readGuidedStartContext(args: {
  ticketId: string;
  shopId: string;
  legacyParked: boolean;
  refusedBayIds?: Iterable<string>;
}): Promise<GuidedStartContext> {
  const [current, plan, baysRes, occupiedBayIds, ticketRes] = await Promise.all([
    readCurrentLocation(args.ticketId),
    readLocationPlan(args.ticketId),
    supabase
      .from('shop_bays')
      .select('id, name, bay_number, is_active, is_locked_out, bay_category, assigned_tech_id')
      .eq('shop_id', args.shopId)
      .order('bay_number', { ascending: true }),
    readShopOccupiedBayIds(args.shopId),
    supabase
      .from('tickets')
      .select('customer_name, parent_ticket_id, rollover_of_ticket_id, vehicle:vehicles(year, make, model)')
      .eq('id', args.ticketId)
      .maybeSingle(),
  ]);
  if (baysRes.error) throw baysRes.error;
  const bayRows = (baysRes.data ?? []) as Array<{
    id: string; name: string | null; bay_number: number | null; is_active: boolean | null;
    is_locked_out: boolean | null; bay_category: string | null; assigned_tech_id: string | null;
  }>;

  const techIds = [...new Set(bayRows.map((b) => b.assigned_tech_id).filter((x): x is string => !!x))];
  const techNames = new Map<string, string>();
  if (techIds.length > 0) {
    const { data } = await supabase.from('technicians').select('id, name').in('id', techIds);
    for (const t of (data ?? []) as Array<{ id: string; name: string | null }>) {
      if (t.name) techNames.set(t.id, t.name.split(' ')[0]);
    }
  }

  const spotLabels = new Map<string, string>();
  if (current?.kind === 'PARKING_SPOT' && current.parkingSpotId) {
    const { data } = await supabase.from('parking_lot_spots').select('id, label')
      .eq('id', current.parkingSpotId).maybeSingle();
    const spot = data as { id: string; label: string } | null;
    if (spot) spotLabels.set(spot.id, spot.label);
  }

  const ticket = ticketRes.data as {
    customer_name?: string | null;
    parent_ticket_id?: string | null;
    rollover_of_ticket_id?: string | null;
    vehicle?: { year?: number | null; make?: string | null; model?: string | null } | null;
  } | null;

  const bays: GuidedStartBay[] = bayRows.map((b) => ({
    id: b.id,
    name: b.name,
    bayNumber: b.bay_number,
    isActive: b.is_active,
    isLockedOut: b.is_locked_out,
    category: b.bay_category,
    techName: b.assigned_tech_id ? techNames.get(b.assigned_tech_id) ?? null : null,
  }));

  // NOV3 (D3): the bay this same car already stands in (held by another day of the repair) is a valid choice.
  const sameVehicleBays = await readSameVehicleHeldBayIds(args.shopId, args.ticketId);
  return {
    current,
    plan,
    bays,
    occupiedBayIds: new Set([...occupiedBayIds].filter((id) => !sameVehicleBays.has(id))),
    legacyParked: args.legacyParked,
    // The server's own rule (vehicle_location_holder <> id): only a dependent
    // ticket's legacy columns stand in for a missing row.
    legacyFallback: !!(ticket?.parent_ticket_id || ticket?.rollover_of_ticket_id),
    refusedBayIds: new Set(args.refusedBayIds ?? []),
    vehicleLabel: vehicleLabelOf(ticket),
    spotLabels,
  };
}

/**
 * Item #14 — the shop's layout for the Guided Start bay picker: bay and spot
 * geometry only (the Birdseye page's rectangles). Occupancy is NOT read here —
 * the picker shows Guided Start's own choices, from physical truth.
 */
export async function readFacilityLayout(shopId: string): Promise<FacilityLayout> {
  const [baysRes, spotsRes] = await Promise.all([
    supabase.from('shop_bays')
      .select('id, name, bay_number, bay_category, is_active, position_x, position_y, width, height')
      .eq('shop_id', shopId),
    supabase.from('parking_lot_spots')
      .select('id, label, position_x, position_y, width, height')
      .eq('shop_id', shopId)
      .eq('is_active', true),
  ]);
  if (baysRes.error) throw baysRes.error;
  if (spotsRes.error) throw spotsRes.error;
  const bayRows = (baysRes.data ?? []) as Array<LayoutRow & { bay_category: string | null; is_active: boolean | null }>;
  const work = bayRows.filter((b) => b.is_active !== false && b.bay_category !== 'parking');
  const parkingBays = bayRows.filter((b) => b.is_active !== false && b.bay_category === 'parking');
  return {
    bays: work.flatMap((b) => {
      const r = rectOf(b, b.name || `Bay ${b.bay_number ?? ''}`.trim());
      return r ? [r] : [];
    }),
    spots: normalizeSpotRows((spotsRes.data ?? []) as LayoutRow[], parkingBays),
  };
}

/** "Judy Bloom’s 2003 Grand Prix" — only the parts that are known. */
/** One parking spot a finishing technician may leave the vehicle in. */
export interface ParkingSpotChoice { id: string; label: string; occupied: boolean }

/**
 * Stage E — the parking spots of a shop and which are PHYSICALLY occupied now
 * (current Location Truth only: a PLANNED spot occupies nothing). Advisory for
 * the picker — finish_my_work's move authority re-decides at commit and never
 * substitutes another spot.
 */
export async function readParkingSpotChoices(shopId: string, ticketId: string): Promise<ParkingSpotChoice[]> {
  const [spots, current] = await Promise.all([
    supabase.from('parking_lot_spots').select('id, label, order_index, is_active').eq('shop_id', shopId).eq('is_active', true),
    supabase.from('vehicle_location_current').select('ticket_id, parking_spot_id')
      .eq('shop_id', shopId).eq('location_kind', 'PARKING_SPOT'),
  ]);
  if (spots.error) throw spots.error;
  if (current.error) throw current.error;
  const occupied = new Set(((current.data ?? []) as Array<{ ticket_id: string; parking_spot_id: string | null }>)
    .filter((r) => r.ticket_id !== ticketId && r.parking_spot_id)
    .map((r) => r.parking_spot_id as string));
  return ((spots.data ?? []) as Array<{ id: string; label: string | null; order_index: number | null }>)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0) || String(a.label).localeCompare(String(b.label), undefined, { numeric: true }))
    .map((s) => ({ id: s.id, label: s.label ?? '?', occupied: occupied.has(s.id) }));
}

export function vehicleLabelOf(row: {
  customer_name?: string | null;
  vehicle?: { year?: number | null; make?: string | null; model?: string | null } | null;
} | null): string | null {
  if (!row) return null;
  const v = row.vehicle ?? null;
  const car = [v?.year, v?.model ?? v?.make].filter(Boolean).join(' ');
  const owner = row.customer_name?.trim();
  if (owner && car) return `${owner}’s ${car}`;
  if (car) return `the ${car}`;
  if (owner) return `${owner}’s vehicle`;
  return null;
}

/**
 * NOV3 (browser proof D3): bays held RIGHT NOW (physical truth) by ANOTHER DAY OF THE SAME VEHICLE as this ticket —
 * where this car already is. They are not "occupied" for this car: the pull-in authority accepts them and moves the
 * row. Floyd's: the Nov 3 day's chooser disabled the bay the Audi stood in because the Nov 2 day held it.
 */
export async function readSameVehicleHeldBayIds(shopId: string, ticketId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('vehicle_location_current')
    .select('ticket_id, bay_id')
    .eq('shop_id', shopId)
    .eq('location_kind', 'BAY');
  if (error) throw error;
  const rows = ((data ?? []) as Array<{ ticket_id: string; bay_id: string | null }>).filter((r) => !!r.bay_id && r.ticket_id !== ticketId);
  if (rows.length === 0) return new Set();
  const { data: veh, error: vErr } = await supabase
    .from('tickets').select('id, vehicle_id').in('id', [ticketId, ...rows.map((r) => r.ticket_id)]);
  if (vErr) throw vErr;
  const vehicleOf = new Map(((veh ?? []) as Array<{ id: string; vehicle_id: string | null }>).map((t) => [t.id, t.vehicle_id]));
  const mine = vehicleOf.get(ticketId) ?? null;
  return new Set(mine ? rows.filter((r) => vehicleOf.get(r.ticket_id) === mine).map((r) => r.bay_id as string) : []);
}
